import _ from 'underscore';
import {
  getTargetedLocationInfo,
  getSupportedLanguages,
  getSearchIdeaModel,
} from '@bingads-webui-campaign-react/copilot-keyword-planner';
import { WIZARD_EVENTS } from '@bingads-webui-react/wizard';
import { LOCATION_CRITERION_TYPE } from '@bingads-webui-campaign-react/search-campaign-data';
import { targetOptions } from '@bingads-webui-campaign-react/search-campaign-wizard-controls';
import {
  CHANNEL_TYPES,
} from '@bingads-webui-campaign-react/keyword-planner-shared';

const getTargetedLocationNameTypeCombo = (location) => {
  const type = _.property('CriterionType')(location);
  return `${type} - ${_.property(['Criterion', `${type}Criterion`, 'Name'])(location)}`;
};
const mapToLocationNameTypeCombo = locations => (_.map(locations, location => getTargetedLocationNameTypeCombo(location)));


export const useSetKeywordSuggestionsData = ({ send, permissions }) => {
  const onRequest = () => {
    send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, {
      keywordSuggestions: null,
    });
  };
  const onSuccess = (rawData) => {
    if (permissions && permissions.dynamic && permissions.dynamic.KeywordPlannerLiteRefactorPhase1) {
      const keywordSuggestions = _.first(rawData.data.value).Keywords || [];
      send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, {
        keywordSuggestions: _.map(keywordSuggestions, keyword => ({
          Keyword: keyword.Keyword.Text,
          RequestId: rawData.traceInfo && rawData.traceInfo.requestId,
        })),
      });
    } else {
      send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, {
        keywordSuggestions: _.flatten(_.map(rawData.value, group => (_.isElement(group.Suggestions) ? [] : group.Suggestions))),
      });
    }
  };
  const onFailed = () => {
    send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, {
      keywordSuggestions: [],
    });
  };

  return {
    onRequest, onSuccess, onFailed,
  };
};

export const handleKeywordSuggestionsWhenChanges = ({
  currentUrl, currentLocationSettings, currentLanguages,
  previousUrl, previousLocation, previousLanguages,
  activity, bundleLocationCriterions,
  onRequest, onSuccess, onFailed, recommendateRequest, getSearchIdea, send, permissions,
}) => {
  const log = activity ? activity.signal : _.noop;
  const targetOption = _.property(['targetOption'])(currentLocationSettings);
  let currentLocation = null;

  if (targetOption === targetOptions.everywhere) {
    currentLocation = [];
  } else if (targetOption === targetOptions.bundle) {
    currentLocation = bundleLocationCriterions;
  } else if (targetOption === targetOptions.specific) {
    currentLocation = _.property(['currentLocations'])(currentLocationSettings);
  }

  log(`onHandleKeywordSuggestions - ${JSON.stringify({
    Url: currentUrl,
    Location: {
      TargetOption: _.property(['targetOption'])(currentLocationSettings),
      TargetedLocationInfo: _.map(currentLocation, (location) => {
        if (location.CriterionType === LOCATION_CRITERION_TYPE.LOCATION) {
          return _.property(['Criterion', 'LocationCriterion'])(location);
        } else if (location.CriterionType === LOCATION_CRITERION_TYPE.RADIUS) {
          return _.property(['Criterion', 'RadiusCriterion'])(location);
        }
        return null;
      }),
    },
    Languages: currentLanguages,
  })}`);

  const isUrlEqual = _.isEqual(currentUrl, previousUrl);
  if (!isUrlEqual) {
    send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, {
      previousUrl: currentUrl,
    });
  }

  const previousLocationNameTypeCombo = mapToLocationNameTypeCombo(previousLocation);
  const currentLocationNameTypeCombo = mapToLocationNameTypeCombo(currentLocation);
  const isLocationEqual = _.isEqual(currentLocationNameTypeCombo, previousLocationNameTypeCombo);
  if (!isLocationEqual) {
    const locationsNeedToAdd = _.difference(currentLocationNameTypeCombo, previousLocationNameTypeCombo);
    const locationsNeedToRemove = _.difference(previousLocationNameTypeCombo, currentLocationNameTypeCombo);
    const locationsAdded = _.filter(currentLocation, location => _.contains(locationsNeedToAdd, getTargetedLocationNameTypeCombo(location)));
    const locationsLeftedOver = _.filter(previousLocation, location => !_.contains(locationsNeedToRemove, getTargetedLocationNameTypeCombo(location)));
    currentLocation = locationsAdded.concat(locationsLeftedOver);
    send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, {
      previousLocation: currentLocation,
    });
  }

  const isLanguagesEqual = _.isEqual(currentLanguages, previousLanguages);
  if (!isLanguagesEqual) {
    send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, {
      previousLanguages: currentLanguages,
    });
  }

  if (!isUrlEqual || !isLocationEqual || !isLanguagesEqual) {
    const targetedLocationInfo = getTargetedLocationInfo(isLocationEqual ? previousLocation : currentLocation, permissions);
    const languages = getSupportedLanguages(isLanguagesEqual ? previousLanguages : currentLanguages, permissions);
    if ((currentUrl && !_.isEmpty(currentUrl.replace(/^(https?:\/\/)/, ''))) && !_.isEmpty(targetedLocationInfo) && !_.isEmpty(languages)) {
      onRequest();
      try {
        if (permissions && permissions.dynamic && permissions.dynamic.KeywordPlannerLiteRefactorPhase1) {
          getSearchIdea({
            model: getSearchIdeaModel({
              url: currentUrl,
              targetedLocationInfo,
              languages,
            }),
            isLanguageDecoupling: true,
            channel: CHANNEL_TYPES.CAMPAIGN_CREATION,
          }).then((rawData) => {
            if (rawData && rawData.data && rawData.data.value && _.first(rawData.data.value)) {
              log('onFetchRecommendationInSetting - has data');
              onSuccess(rawData);
            } else {
              log('onFetchRecommendationInSetting - no data');
              onFailed();
            }
          }).catch((error) => {
            log(`onFetchRecommendationInSetting - has error - ${JSON.stringify({
              ErrorStatus: error && error.status,
            })}`);
            onFailed();
          });
        } else {
          recommendateRequest({
            url: currentUrl,
            targetedLocationInfo,
            languages,
          }).then((rawData) => {
            if (rawData && rawData.value) {
              log('onFetchRecommendationInSetting - has data');
              onSuccess(rawData);
            } else {
              log('onFetchRecommendationInSetting - no data');
              onFailed();
            }
          }).catch((error) => {
            log(`onFetchRecommendationInSetting - has error - ${JSON.stringify({
              ErrorStatus: error && error.status,
            })}`);
            onFailed();
          });
        }
      } catch (error) {
        onFailed();
      }
    } else {
      onFailed();
    }
  }
};
